<template>
  <q-card
    flat
    bordered
    class="re-markdown-editor"
    :class="[
      {
        'bg-disabled': isDisabled,
        'bg-base': !isDisabled,
        're-markdown-editor--error': hasError,
      },
    ]"
    :aria-disabled="isDisabled || undefined"
    :aria-required="required || undefined"
    v-bind="$attrs"
  >
    <q-toolbar v-if="showToolbar">
      <ReMarkdownEditorActions
        size="sm"
        show-heading-label
        :disable="isDisabled"
        :heading-action-color="headingActionColor"
        :active-heading-option="activeHeadingOption"
        :heading-options="headingOptions"
        :toolbar-actions="toolbarActions"
      />
    </q-toolbar>

    <q-separator v-if="showToolbar" />

    <div
      v-if="editor"
      class="re-markdown-editor__content relative-position"
    >
      <EditorContent
        :editor="editor"
        @focusin="handleFocus"
        @focusout="handleBlur()"
      />

      <div
        v-if="showPlaceholder"
        class="absolute-top-left q-ma-md text-secondary no-pointer-events"
      >
        {{ placeholder }}
      </div>

      <div
        v-if="showFloatingActions"
        ref="floatingActionsEl"
        class="re-markdown-editor__floating-actions row q-gutter-xs bg-base shadow-2"
        :style="floatingActionsStyle"
        @mousedown.prevent
      >
        <ReMarkdownEditorActions
          size="xs"
          hide-heading
          :disable="isDisabled"
          :heading-action-color="headingActionColor"
          :active-heading-option="activeHeadingOption"
          :heading-options="headingOptions"
          :toolbar-actions="floatingToolbarActions"
        />
      </div>
    </div>

    <div
      v-if="hasError || normalizedMaxChars"
      class="row items-center justify-between q-px-md q-pb-sm text-caption"
    >
      <span class="text-negative">
        {{ errorMessage }}
      </span>

      <span
        v-if="normalizedMaxChars"
        class="q-ml-md"
        :class="isOverMaxChars ? 'text-negative' : 'text-secondary'"
      >
        {{ characterCount }}/{{ normalizedMaxChars }}
      </span>
    </div>
  </q-card>
</template>

<script setup lang="ts">
import type { MarkdownHeadingSize } from '@/elements/markdown'
import { Markdown } from '@tiptap/markdown'
import StarterKit from '@tiptap/starter-kit'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import { debounce as lodashDebounce } from 'lodash-es'
import { useFormChild } from 'quasar'

import { computed, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue'
import { useReadOnly } from '@/composables/useReadOnly'
import { getMarkdownHeadingClass } from '@/elements/markdown'
import reRules from '@/plugins/rules'

import ReMarkdownEditorActions from './ReMarkdownEditorActions.vue'

interface FloatingActionsEditor {
  state: {
    selection: {
      empty: boolean
      from: number
      to: number
    }
  }
  view: {
    dom: HTMLElement
    coordsAtPos: (pos: number) => {
      left: number
      right: number
      top: number
    }
  }
}

const {
  hideToolbar = false,
  placeholder = '',
  required = false,
  disable = false,
  debounce = 0,
  maxChars = undefined,
  headingSize = 'default',
} = defineProps<{
  hideToolbar?: boolean
  placeholder?: string
  required?: boolean
  disable?: boolean
  debounce?: number
  maxChars?: number | string
  headingSize?: MarkdownHeadingSize
}>()

defineOptions({ inheritAttrs: false })

const model = defineModel<string | null>('modelValue', { default: '' })

const { isReadOnly } = useReadOnly()

const errorMessage = ref('')
const currentMarkdown = ref(model.value ?? '')
const isEditorEmpty = ref(!(model.value ?? '').length)
const wasFocused = ref(false)
const floatingActionsEl = useTemplateRef<HTMLElement | null>('floatingActionsEl')
const floatingActionsStyle = ref<Record<string, string>>({})
const showFloatingActions = ref(false)

const hasError = computed(() => !!errorMessage.value)
const isDisabled = computed(() => isReadOnly.value || disable)

const normalizedMaxChars = computed(() => {
  const limit = Number(maxChars)
  return Number.isFinite(limit) && limit > 0 ? limit : undefined
})
const characterCount = computed(() => currentMarkdown.value.length)
const isOverMaxChars = computed(() => {
  return normalizedMaxChars.value !== undefined && characterCount.value > normalizedMaxChars.value
})
const showPlaceholder = computed(() => !!placeholder && isEditorEmpty.value && !isDisabled.value)
const showToolbar = computed(() => !!editor.value && !hideToolbar)
const canShowFloatingActions = computed(() => !!editor.value && !hideToolbar)
const markdownContentClass = computed(() => {
  return [
    're-markdown',
    're-markdown-editor__prosemirror',
    'q-pa-md',
    getMarkdownHeadingClass(headingSize),
  ].filter(Boolean).join(' ')
})

function resetValidation() {
  errorMessage.value = ''
}

function validate(value: string | null = currentMarkdown.value) {
  if (isDisabled.value) {
    resetValidation()
    return true
  }

  const requiredResult = required ? reRules.required((value ?? '').trim()) : true
  if (requiredResult !== true) {
    errorMessage.value = requiredResult
    return false
  }

  if (normalizedMaxChars.value !== undefined) {
    const maxLengthResult = reRules.maxLength(normalizedMaxChars.value)(value ?? '')
    if (maxLengthResult !== true) {
      errorMessage.value = maxLengthResult
      return false
    }
  }

  errorMessage.value = ''
  return true
}

function handleFocus() {
  wasFocused.value = true
}

function handleBlur(value: string | null = currentMarkdown.value) {
  if (wasFocused.value && (required || normalizedMaxChars.value))
    validate(value)
}

const updateModel = lodashDebounce(
  (markdown: string) => {
    if (markdown !== (model.value ?? ''))
      model.value = markdown
  },
  debounce,
)

const editor = useEditor({
  extensions: [
    StarterKit,
    Markdown.configure({
      markedOptions: {
        gfm: true,
      },
    }),
  ],
  content: model.value ?? '',
  contentType: 'markdown',
  editable: !isDisabled.value,
  editorProps: {
    attributes: {
      class: markdownContentClass.value,
    },
  },
  onUpdate: ({ editor }) => {
    isEditorEmpty.value = editor.isEmpty
    const markdown = editor.getMarkdown()
    currentMarkdown.value = markdown
    updateModel(markdown)
    if (hasError.value)
      validate(markdown)
  },
  onFocus: handleFocus,
  onBlur: ({ event }) => {
    if (event?.relatedTarget && floatingActionsEl.value?.contains(event.relatedTarget as Node))
      return

    showFloatingActions.value = false
    updateModel.flush()
    handleBlur(currentMarkdown.value)
  },
  onSelectionUpdate: ({ editor }) => updateFloatingActions(editor),
})

function updateFloatingActions(currentEditor: FloatingActionsEditor | null | undefined = editor.value) {
  const selection = currentEditor?.state.selection

  if (!currentEditor || !canShowFloatingActions.value || isDisabled.value || !selection || selection.empty) {
    showFloatingActions.value = false
    return
  }

  const parentRect = currentEditor.view.dom.parentElement?.getBoundingClientRect()
  if (!parentRect) {
    showFloatingActions.value = false
    return
  }

  const start = currentEditor.view.coordsAtPos(selection.from)
  const end = currentEditor.view.coordsAtPos(selection.to)
  const center = (start.left + end.right) / 2 - parentRect.left
  const maxLeft = Math.max(parentRect.width - (floatingActionsEl.value?.offsetWidth ?? 320) - 8, 8)

  floatingActionsStyle.value = {
    left: `${Math.min(Math.max(center, 8), maxLeft)}px`,
    top: `${Math.min(start.top, end.top) - parentRect.top}px`,
  }
  showFloatingActions.value = true
}

watch(model, (value) => {
  const nextMarkdown = value ?? ''

  if (!editor.value || editor.value.getMarkdown() === nextMarkdown) {
    currentMarkdown.value = nextMarkdown
    if (hasError.value)
      validate(nextMarkdown)
    return
  }

  updateModel.cancel()
  editor.value.commands.setContent(nextMarkdown, {
    contentType: 'markdown',
    emitUpdate: false,
  })
  currentMarkdown.value = nextMarkdown
  isEditorEmpty.value = editor.value.isEmpty
  if (hasError.value)
    validate(nextMarkdown)
})

watch(isDisabled, (value) => {
  editor.value?.setEditable(!value)
  if (value)
    resetValidation()
})

onBeforeUnmount(() => updateModel.flush())

watch(markdownContentClass, (value) => {
  editor.value?.setOptions({
    editorProps: {
      attributes: {
        class: value,
      },
    },
  })
})

watch(
  () => required,
  (value) => {
    if (!value)
      resetValidation()
    else if (hasError.value)
      validate()
  },
)

watch(normalizedMaxChars, () => {
  if (hasError.value || isOverMaxChars.value)
    validate()
})

const focus = () => editor.value?.commands.focus()

useFormChild({
  validate,
  resetValidation,
  requiresQForm: false,
})

defineExpose({
  hasError,
  validate,
  resetValidation,
  focus,
  blur: () => editor.value?.commands.blur(),
})

const headingOptions = [
  {
    name: 'paragraph',
    label: 'Paragraph',
    icon: 'mdi-format-paragraph',
    isActive: () => editor.value?.isActive('paragraph') ?? false,
    run: () => editor.value?.chain().focus().setParagraph().run(),
  },
  {
    name: 'heading-1',
    label: 'Heading 1',
    icon: 'mdi-format-header-1',
    isActive: () => editor.value?.isActive('heading', { level: 1 }) ?? false,
    run: () => editor.value?.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    name: 'heading-2',
    label: 'Heading 2',
    icon: 'mdi-format-header-2',
    isActive: () => editor.value?.isActive('heading', { level: 2 }) ?? false,
    run: () => editor.value?.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    name: 'heading-3',
    label: 'Heading 3',
    icon: 'mdi-format-header-3',
    isActive: () => editor.value?.isActive('heading', { level: 3 }) ?? false,
    run: () => editor.value?.chain().focus().toggleHeading({ level: 3 }).run(),
  },
]

const activeHeadingOption = computed(() => {
  return headingOptions.find(option => option.isActive()) ?? headingOptions[0]
})

const headingActionColor = computed(() => {
  return activeHeadingOption.value.name === 'paragraph' ? 'secondary' : 'primary'
})

const toolbarActions = computed(() => [
  {
    name: 'bold',
    label: 'Bold',
    icon: 'mdi-format-bold',
    isActive: () => editor.value?.isActive('bold') ?? false,
    run: () => editor.value?.chain().focus().toggleBold().run(),
  },
  {
    name: 'italic',
    label: 'Italic',
    icon: 'mdi-format-italic',
    isActive: () => editor.value?.isActive('italic') ?? false,
    run: () => editor.value?.chain().focus().toggleItalic().run(),
  },
  {
    name: 'strike',
    label: 'Strike',
    icon: 'mdi-format-strikethrough',
    isActive: () => editor.value?.isActive('strike') ?? false,
    run: () => editor.value?.chain().focus().toggleStrike().run(),
  },
  {
    name: 'bulletList',
    label: 'Bullet list',
    icon: 'mdi-format-list-bulleted',
    isActive: () => editor.value?.isActive('bulletList') ?? false,
    run: () => editor.value?.chain().focus().toggleBulletList().run(),
  },
  {
    name: 'orderedList',
    label: 'Ordered list',
    icon: 'mdi-format-list-numbered',
    isActive: () => editor.value?.isActive('orderedList') ?? false,
    run: () => editor.value?.chain().focus().toggleOrderedList().run(),
  },
  {
    name: 'blockquote',
    label: 'Quote',
    icon: 'mdi-format-quote-close',
    isActive: () => editor.value?.isActive('blockquote') ?? false,
    run: () => editor.value?.chain().focus().toggleBlockquote().run(),
  },
])

const floatingToolbarActions = computed(() => {
  return toolbarActions.value.filter(action => ['bold', 'italic', 'strike'].includes(action.name))
})
</script>

<style lang="scss" scoped>
.re-markdown-editor__content {
  min-height: 120px;
}

.re-markdown-editor {
  :deep(.q-toolbar) {
    flex-wrap: wrap;
  }

  :deep(.re-markdown-editor__prosemirror) {
    min-height: 120px;
    outline: none;
  }
}

.re-markdown-editor__floating-actions {
  position: absolute;
  flex-wrap: nowrap;
  max-width: calc(100% - 16px);
  overflow-x: auto;
  padding: 4px;
  border-radius: 4px;
  transform: translateY(calc(-100% - 8px));
  z-index: 1;
}

.re-markdown-editor--error {
  border-color: var(--q-negative);
  outline: 1px solid var(--q-negative);
  outline-offset: -1px;
}
</style>
